<script module lang="ts">
	import { usePointer } from '@vpap_svelte/core';
	import { Html } from '../index.js';
	import { quadtree } from 'd3';

	import type { Snippet } from 'svelte';
	import type { Numeric, ScaleContinuousNumeric, ScaleTime } from 'd3';
	import { watch } from '@vpap_svelte/internal';

	/**
	 * Represents the data for a selection with the pointer
	 */
	export interface PointerSelection<DataRecord> {
		/**
		 * The selected record (or undefined if there is no selected record)
		 */
		selected: DataRecord | undefined;
		/**
		 * The pointer itself
		 */
		pointer: ReturnType<typeof usePointer>;
	}

	/**
	 * Base props for the pointer
	 */
	export interface BasePointerProps<DataRecord> {
		/**
		 * The data you want users to select with the pointer
		 */
		data: DataRecord[];
		/**
		 * The maximum offset, in pixels, that a pointer can be away from the nearest point while triggering
		 * a selection
		 */
		maxOffset?: number;
		/**
		 * A callback that runs every time new data (or no data) is selected
		 */
		onSelectionChange?: (record: DataRecord | undefined) => void;
		/**
		 * A callback that runs every time the pointer's position or the `isInside` state of the pointer changes
		 */
		onMovement?: (pointer: ReturnType<typeof usePointer>) => void;
		/**
		 * This is where you can render any contents like a tooltip. It doesn't handle positioning but provides you with the necessary parts to position and label your tooltip.
		 */
		children?: Snippet<[PointerSelection<DataRecord>]>;
	}

	type PointerXGet<DataRecord> = { xGet: (record: DataRecord) => number };

	/**
	 * Props for the x-axis handling of the pointer
	 */
	interface PointerXScaleProps<DataRecord> {
		/**
		 * The x accessor (to convert a record into a number or numeric point along the x domain)
		 * @param datum A single record
		 * @param index The index of that record within the whole location
		 * @param data The entire dataset
		 */
		xAccessor: (datum: DataRecord) => number | Numeric;
		/**
		 * The x scale
		 */
		xScale: ScaleContinuousNumeric<number | Numeric, number> | ScaleTime<number | Numeric, number>;
	}

	export type PointerXProps<DataRecord> = PointerXGet<DataRecord> | PointerXScaleProps<DataRecord>;

	type PointerYGet<DataRecord> = { yGet: (record: DataRecord) => number };
	interface PointerYScaleProps<DataRecord> {
		/**
		 * The y accessor (to convert a record into a number or numeric point along the y domain)
		 * @param datum A single record
		 * @param index The index of that record within the whole location
		 * @param data The entire dataset
		 */
		yAccessor: (datum: DataRecord) => number | Numeric;
		/**
		 * The y scale
		 */
		yScale: ScaleContinuousNumeric<number | Numeric, number> | ScaleTime<number | Numeric, number>;
	}
	export type PointerYProps<DataRecord> = PointerYGet<DataRecord> | PointerYScaleProps<DataRecord>;

	/**
	 * The full set of props for the pointer
	 */
	export type PointerProps<DataRecord> = BasePointerProps<DataRecord> &
		(
			| PointerXProps<DataRecord>
			| PointerYProps<DataRecord>
			| (PointerXProps<DataRecord> & PointerYProps<DataRecord>)
		);
</script>

<script lang="ts" generics="DataRecord">
	// adding in xAccessor / yAccessor explicitly here doesn't play nicely with typescript since
	// it doesn't know if those exist
	let {
		data,
		maxOffset,
		children,
		onSelectionChange,
		onMovement,
		...rest
	}: PointerProps<DataRecord> = $props();
	let xGet = $derived.by(() => {
		if ('xGet' in rest) return rest.xGet;
		if ('xAccessor' in rest) return (datum: DataRecord) => rest.xScale(rest.xAccessor(datum));
		return () => 0;
	});
	let yGet = $derived.by(() => {
		if ('yGet' in rest) return rest.yGet;
		if ('yAccessor' in rest) return (datum: DataRecord) => rest.yScale(rest.yAccessor(datum));
		return () => 0;
	});

	let region: Element | null = $state(null);
	let pointer = usePointer({ target: () => region });
	// for detecting points near the pointer
	let detector = $derived(quadtree<DataRecord>(data, xGet, yGet));
	let nearestPoint = $derived.by(() => {
		if (pointer.offset === undefined) return undefined;
		return detector.find(pointer.offset[0], pointer.offset[1], maxOffset);
	});

	watch(
		() => nearestPoint,
		() => {
			if (onSelectionChange !== undefined) {
				onSelectionChange(nearestPoint);
			}
		},
	);

	watch([() => pointer.current, () => pointer.isInside], () => {
		if (onMovement !== undefined) {
			onMovement(pointer);
		}
	});
</script>

<!-- we hide this bit from screenreaders since it's a pointer interaction only -->
<Html aria-hidden="true">
	<div class="pointer-region" bind:this={region}>
		{#if children}
			{@render children({ selected: nearestPoint, pointer })}
		{/if}
	</div>
</Html>

<style>
	.pointer-region {
		position: absolute;
		width: 100%;
		height: 100%;
	}
</style>
